/**
 * What became of a working-location change the person answered for.
 *
 * Three outcomes and not a boolean, because two of them are "the day did not
 * change" and they call for opposite things to be said: a day held for
 * approval is on its way, and a day that failed to write is not.
 */
export type LocationChangeOutcome = "changed" | "awaiting_approval" | "failed";

/** What to do about the person's dedicated desk once the day is answered. */
export type DeskAction = "release" | "explain" | "nothing";

/**
 * Whether to release the desk for the day, explain why it was not released,
 * or say nothing at all.
 *
 * `wantsRelease` is the person having a desk to release here and having
 * chosen to release it.
 *
 * The day only really changed on `changed`, and that is the only outcome that
 * frees the desk: handing it away while a leave request is still waiting
 * would free the desk of someone who may yet be coming in. Of the two that
 * did not change it, only the waiting one is worth a word - a write that
 * failed has already reported itself, and saying the desk is waiting on an
 * approval would promise one that is never coming.
 */
export const deskActionAfterChange = (
  outcome: LocationChangeOutcome,
  wantsRelease: boolean
): DeskAction => {
  if (!wantsRelease) {
    return "nothing";
  }
  switch (outcome) {
    case "changed":
      return "release";
    case "awaiting_approval":
      return "explain";
    case "failed":
      return "nothing";
  }
};
